import { Lock } from 'lucide-react';
import {
  OUTBOUND_CALL_OUTCOME_LABELS,
  WINDOW_DAYS,
  type CampaignSpec,
  type SuccessCondition,
  type WindowDay,
} from '@vagusx/shared';
import type { AudienceCatalogEntry, ContactPolicy } from '@vagusx/clients';
import { Accordion } from '@/components/ui/accordion';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { MultiSelect } from '@/components/custom/multi-select';
import { Field } from './Field';
import { Section } from './Section';
import { describeLimits, describeSuccess, describeWindow, SUCCESS_LABELS } from './spec';

/**
 * The parts of a campaign that are not about one step.
 *
 * A settings list: every row states its current value on the right, so the
 * whole campaign is readable without opening anything, and opening a row is
 * how you change it rather than how you find out what it says.
 */

const DAY_LABELS: Record<WindowDay, string> = {
  monday: 'Mon',
  tuesday: 'Tue',
  wednesday: 'Wed',
  thursday: 'Thu',
  friday: 'Fri',
  saturday: 'Sat',
  sunday: 'Sun',
};

/** The success shapes this editor can render. Anything else is shown read-only. */
const EDITABLE_SUCCESS = ['appointment_booked', 'appointment_confirmed', 'outcome'] as const;

function isEditable(success: SuccessCondition): boolean {
  return (EDITABLE_SUCCESS as readonly string[]).includes(success.kind);
}

interface CampaignSettingsProps {
  spec: CampaignSpec;
  onSpecChange: (spec: CampaignSpec) => void;
  description: string;
  onDescriptionChange: (description: string) => void;
  priority: number;
  onPriorityChange: (priority: number) => void;
  endWhenNoLongerQualifies: boolean;
  onEndWhenNoLongerQualifiesChange: (value: boolean) => void;
  policy: ContactPolicy | undefined;
  catalogEntry: AudienceCatalogEntry | undefined;
  /**
   * Which section is open. Controlled by the editor rather than the accordion
   * itself, so clicking a sentence in the text panel can open the setting that
   * sentence describes — the panel is the campaign's table of contents, and a
   * link that scrolled to a collapsed row would land on nothing.
   */
  open: string;
  onOpenChange: (value: string) => void;
}

export function CampaignSettings({
  spec,
  onSpecChange,
  description,
  onDescriptionChange,
  priority,
  onPriorityChange,
  endWhenNoLongerQualifies,
  onEndWhenNoLongerQualifiesChange,
  policy,
  catalogEntry,
  open,
  onOpenChange,
}: CampaignSettingsProps): React.ReactElement {
  const patch = (over: Partial<CampaignSpec>): void => onSpecChange({ ...spec, ...over });
  const outcomeOptions = OUTBOUND_CALL_OUTCOME_LABELS.map((o) => ({
    value: o.value,
    label: o.label,
  }));

  const stopSummary = spec.stopOn.length
    ? `${spec.stopOn.length} ${spec.stopOn.length === 1 ? 'outcome' : 'outcomes'}`
    : 'Nothing';

  return (
    <Accordion
      type="single"
      collapsible
      value={open}
      onValueChange={onOpenChange}
      className="divide-y rounded-lg border"
    >
      {/* ── Calling hours ──────────────────────────────────────────── */}
      <Section value="hours" title="Calling hours" summary={describeWindow(spec.window)}>
        <Field label="Calling days">
          <ToggleGroup
            type="multiple"
            variant="outline"
            size="sm"
            value={spec.window.days}
            onValueChange={(days) => {
              // An empty day list fails `min(1)` and would make the campaign
              // unpublishable from a control that looked like it worked.
              const chosen = days.filter((d): d is WindowDay =>
                (WINDOW_DAYS as readonly string[]).includes(d)
              );
              if (chosen.length) patch({ window: { ...spec.window, days: chosen } });
            }}
          >
            {WINDOW_DAYS.map((day) => (
              <ToggleGroupItem key={day} value={day} aria-label={day} className="px-2.5">
                {DAY_LABELS[day]}
              </ToggleGroupItem>
            ))}
          </ToggleGroup>
        </Field>

        <Field label="Calling hours">
          <Input
            type="time"
            className="h-9 w-32"
            aria-label="Start of calling window"
            value={spec.window.start}
            onChange={(e) => patch({ window: { ...spec.window, start: e.target.value } })}
          />
          <span className="text-sm text-muted-foreground">and</span>
          <Input
            type="time"
            className="h-9 w-32"
            aria-label="End of calling window"
            value={spec.window.end}
            onChange={(e) => patch({ window: { ...spec.window, end: e.target.value } })}
          />
        </Field>

        <Field label="Also respect">
          <Switch
            id="use-location-hours"
            checked={spec.window.useLocationHours}
            onCheckedChange={(useLocationHours) =>
              patch({ window: { ...spec.window, useLocationHours } })
            }
          />
          <label htmlFor="use-location-hours" className="text-sm">
            Stay inside the location's opening hours
          </label>
        </Field>

        {policy && (
          <p className="mt-2 flex items-start gap-2 rounded-md bg-muted/50 px-3 py-2 text-xs text-muted-foreground">
            <Lock className="mt-0.5 h-3 w-3 shrink-0" />
            <span>
              Calls never happen outside {policy.quietHoursStart}–{policy.quietHoursEnd}, set once
              for the organization. A campaign narrows that; it can never widen it.
            </span>
          </p>
        )}
      </Section>

      {/* ── Success ────────────────────────────────────────────────── */}
      <Section value="success" title="Success" summary={describeSuccess(spec.success)}>
        {isEditable(spec.success) ? (
          <>
            <Field
              label="Success means"
              hint="The first two read the appointment record, never what the agent says on the call."
            >
              <Select
                value={spec.success.kind}
                onValueChange={(kind) =>
                  patch({
                    success:
                      kind === 'outcome'
                        ? { kind: 'outcome', anyOf: ['scheduled'] }
                        : ({ kind } as SuccessCondition),
                  })
                }
              >
                <SelectTrigger className="h-9 w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {EDITABLE_SUCCESS.map((kind) => (
                    <SelectItem key={kind} value={kind}>
                      {SUCCESS_LABELS[kind]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>

            {spec.success.kind === 'outcome' && (
              <Field label="Any of these">
                <div className="w-full">
                  <MultiSelect
                    options={outcomeOptions}
                    selected={spec.success.anyOf}
                    onChange={(anyOf) => {
                      if (anyOf.length) patch({ success: { kind: 'outcome', anyOf } });
                    }}
                    placeholder="Choose at least one outcome"
                  />
                </div>
              </Field>
            )}
          </>
        ) : (
          /*
            A combined or data-collected condition, published by the compiler or
            by hand. Read-only beats a control that would silently flatten it on
            the next publish.
          */
          <p className="py-2 text-sm text-muted-foreground">
            This campaign's success is a <code>{spec.success.kind}</code> condition, which this
            editor does not change. Publishing keeps it exactly as it is.
          </p>
        )}
      </Section>

      {/* ── Stop outcomes ──────────────────────────────────────────── */}
      <Section value="stop" title="Stop the sequence on" summary={stopSummary}>
        <Field
          label="Any of these"
          hint="These end the run from any step, whatever that step continues on."
        >
          <div className="w-full">
            <MultiSelect
              options={outcomeOptions}
              selected={spec.stopOn}
              onChange={(stopOn) => patch({ stopOn })}
              placeholder="Nothing stops the run early"
            />
          </div>
        </Field>
      </Section>

      {/* ── Limits ─────────────────────────────────────────────────── */}
      <Section value="limits" title="Limits" summary={describeLimits(spec, priority)}>
        <Field label="Max calls" htmlFor="max-attempts">
          <Input
            id="max-attempts"
            type="number"
            min={1}
            max={50}
            className="h-9 w-20"
            value={spec.maxTotalAttempts}
            onChange={(e) => {
              const n = Number(e.target.value);
              if (Number.isFinite(n) && n >= 1 && n <= 50) patch({ maxTotalAttempts: n });
            }}
          />
        </Field>

        <Field label="Give up after" htmlFor="expires-after" hint="Blank means never give up.">
          <Input
            id="expires-after"
            type="number"
            min={1}
            placeholder="never"
            className="h-9 w-20"
            value={spec.expiresAfterDays ?? ''}
            onChange={(e) => {
              const raw = e.target.value;
              if (raw === '') return patch({ expiresAfterDays: null });
              const n = Number(raw);
              if (Number.isFinite(n) && n >= 1) patch({ expiresAfterDays: n });
            }}
          />
          <span className="text-sm text-muted-foreground">days</span>
        </Field>

        <Field
          label="Priority"
          htmlFor="priority"
          hint="Breaks the tie when two campaigns want the same patient — the loser is deferred, never dropped."
        >
          <Input
            id="priority"
            type="number"
            min={0}
            max={100}
            className="h-9 w-20"
            value={priority}
            onChange={(e) => {
              const n = Number(e.target.value);
              if (Number.isFinite(n) && n >= 0 && n <= 100) onPriorityChange(n);
            }}
          />
        </Field>
      </Section>

      {/* ── Details ────────────────────────────────────────────────── */}
      <Section value="details" title="Details" summary={description.trim() || 'No description'}>
        <Field label="Description" htmlFor="campaign-description">
          <Input
            id="campaign-description"
            placeholder="What this campaign is for"
            maxLength={500}
            className="h-9 w-full"
            value={description}
            onChange={(e) => onDescriptionChange(e.target.value)}
          />
        </Field>

        <Field
          label="If they stop qualifying"
          htmlFor="end-on-requalify"
          hint={
            catalogEntry?.recheckable === false
              ? 'Not available for this audience: it matches an event inside a moving window, so "no longer matches" says nothing about the patient — every run would end a tick after it started.'
              : 'Off by default. Once a journey has started we let it finish, even if the referral closes or the appointment is cancelled.'
          }
        >
          <Switch
            id="end-on-requalify"
            disabled={catalogEntry?.recheckable === false}
            checked={endWhenNoLongerQualifies}
            onCheckedChange={onEndWhenNoLongerQualifiesChange}
          />
          <label htmlFor="end-on-requalify" className="text-sm">
            End the run when the patient stops qualifying
          </label>
        </Field>
      </Section>
    </Accordion>
  );
}
